import { useEffect, useState } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { animate } from 'animejs';

/**
 * System Status Scrubber
 * Persistent scroll progress indicator with status updates
 * Based on scroll.md Pattern 6: "System Status Scrubber"
 */

interface ScrollSection {
    id: string;
    label: string;
    threshold: number; // 0-1 (percentage of page)
    status: string;
}

const SCROLL_SECTIONS: ScrollSection[] = [
    { id: 'hero', label: 'INIT', threshold: 0, status: 'INITIALIZING...' },
    { id: 'detection', label: 'DET', threshold: 0.15, status: 'SYSTEM ONLINE' },
    { id: 'alert', label: 'NET', threshold: 0.35, status: 'DETECTION ACTIVE' },
    { id: 'timeline', label: 'TL', threshold: 0.50, status: 'NETWORK SYNCED' },
    { id: 'stats', label: 'DATA', threshold: 0.65, status: 'ANALYZING DATA' },
    { id: 'demo', label: 'SIM', threshold: 0.80, status: 'ALL SYSTEMS NOMINAL' },
    { id: 'complete', label: 'OK', threshold: 0.95, status: 'MISSION COMPLETE' },
];

export function SystemStatusScrubber() {
    const [scrollProgress, setScrollProgress] = useState(0);
    const [activeSection, setActiveSection] = useState(0);
    const reducedMotion = useReducedMotion();

    useEffect(() => {
        const handleScroll = () => {
            const winScroll = window.scrollY;
            const height = document.documentElement.scrollHeight - window.innerHeight;
            const progress = height > 0 ? winScroll / height : 0;

            setScrollProgress(progress);

            // Find active section
            const currentSection = SCROLL_SECTIONS.reduce((acc, section, index) => {
                return progress >= section.threshold ? index : acc;
            }, 0);

            setActiveSection(currentSection);
        };

        handleScroll(); // Initial calculation
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Pulse animation on active marker
    useEffect(() => {
        if (reducedMotion) return;

        const activeMarker = document.querySelector('.section-marker.active');
        if (activeMarker) {
            animate(activeMarker, {
                scale: [1, 1.3, 1],
                duration: 600,
                easing: 'easeOutQuad'
            });
        }
    }, [activeSection, reducedMotion]);

    const currentStatus = SCROLL_SECTIONS[activeSection]?.status || 'INITIALIZING...';

    return (
        <div
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
            aria-hidden="true"
        >
            {/* Scrubber Container */}
            <div className="scrubber-container flex items-center gap-4 px-6 py-3 rounded-full backdrop-blur-md border"
                style={{
                    background: 'rgba(17, 17, 17, 0.9)',
                    borderColor: 'var(--blueprint-line)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5), 0 0 10px rgba(0, 212, 255, 0.1)',
                }}
            >
                {/* Section Markers */}
                <div className="flex items-center gap-2">
                    {SCROLL_SECTIONS.map((section, index) => (
                        <div
                            key={section.id}
                            className={`section-marker transition-all duration-300 ${index === activeSection ? 'active' : ''
                                }`}
                            style={{
                                width: index === activeSection ? '12px' : '8px',
                                height: index === activeSection ? '12px' : '8px',
                                borderRadius: '50%',
                                background: index <= activeSection
                                    ? 'var(--neon-cyan)'
                                    : 'var(--blueprint-line)',
                                boxShadow: index === activeSection
                                    ? '0 0 8px var(--neon-cyan), 0 0 4px var(--neon-cyan)'
                                    : 'none',
                                transform: index === activeSection && !reducedMotion ? 'scale(1)' : 'scale(1)',
                            }}
                            title={section.label}
                        />
                    ))}
                </div>

                {/* Progress Bar */}
                <div className="relative w-32 h-1 bg-gray-800 rounded-full overflow-hidden">
                    <div
                        className="absolute inset-y-0 left-0 transition-all duration-300"
                        style={{
                            width: `${scrollProgress * 100}%`,
                            background: 'linear-gradient(90deg, var(--neon-cyan), var(--tech-green))',
                            boxShadow: '0 0 6px var(--neon-cyan)',
                        }}
                    />
                </div>

                {/* Status Text */}
                <div
                    className="font-mono text-xs tracking-wider min-w-[180px] text-right"
                    style={{
                        color: 'var(--neon-cyan)',
                        textShadow: '0 0 4px var(--neon-cyan)',
                    }}
                >
                    {currentStatus}
                </div>

                {/* Percentage (Optional) */}
                <div
                    className="font-mono text-xs opacity-50 tabular-nums"
                    style={{ color: 'var(--blueprint-line-light)' }}
                >
                    {Math.round(scrollProgress * 100)}%
                </div>
            </div>
        </div>
    );
}

export default SystemStatusScrubber;
